import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import Image from '../../types/image';
import { RootState } from '../../reducer';
import { AccountCircle } from '../icons';

// Spacing is the call site's job (use gap-*), not baked in here: a hardcoded margin
// can't be removed by a caller and the old mt-[1.6px] nudge fought vertical centering.
const sizeClasses = {
  xs: 'w-6 h-6 rounded-full shrink-0',
  small: 'w-10 h-10 rounded-full shrink-0',
  medium: 'w-14 h-14 rounded-full shrink-0',
  large: 'w-20 h-20 rounded-full shrink-0',
} as const;

interface ProfileImgProps {
  userName: string | undefined;
  imgKey?: number | string;
  imgSize?: 'xs' | 'small' | 'medium' | 'large';
  /**
   * Rendered in place of the picture when this user has none. Defaults to a person
   * glyph at the same size.
   *
   * The fallback lives here rather than at the call site because this is the only
   * place that knows whether a picture was actually found. Call sites used to guess
   * from an unrelated payload field (`user_image` / `employee_image`), which is not
   * the source the picture comes from - so they could show an icon for someone who
   * has a photo, or render an empty <img> box for someone who doesn't.
   */
  fallback?: ReactNode;
}

const ProfileImg: React.FC<ProfileImgProps> = ({ userName, imgKey, imgSize, fallback }) => {
  const image = useSelector((state: RootState) => state.image);
  const imgSrc = image?.all_images.find((s: Image) => s.userName === userName)?.image;
  const className = sizeClasses[imgSize || 'small'];

  if (imgSrc === undefined || imgSrc === '') {
    return (
      <span className={`${className} inline-flex items-center justify-center text-muted`} aria-hidden="true">
        {fallback ?? <AccountCircle className="h-full w-full" />}
      </span>
    );
  }

  return (
    <img src={imgSrc} key={imgKey} className={className} alt="" />
  );
}

export default React.memo(ProfileImg);
